import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import type { AvatarColor } from '@/types';
import { useTeamStore } from '@/store/useTeamStore';
import { AvatarPicker } from './AvatarPicker';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { X } from 'lucide-react';

const schema = z.object({
  name: z.string().min(1, 'Name ist erforderlich'),
  role: z.string().min(1, 'Aufgabe ist erforderlich'),
  description: z.string().min(1, 'Beschreibung ist erforderlich'),
});

type FormData = z.infer<typeof schema>;

interface InitialData {
  id?: string;
  name?: string;
  role?: string;
  description?: string;
  values?: string[];
  typicalPhrases?: string[];
  avatarEmoji?: string;
  avatarColor?: AvatarColor;
  appearanceDescription?: string;
  preGeneratedAvatarUrl?: string;
}

interface Props {
  initial?: InitialData;
  onSave: () => void;
  onCancel: () => void;
  onMemberAdded?: (memberId: string) => void;
}

export function MemberForm({ initial, onSave, onCancel, onMemberAdded }: Props) {
  const { addMember, updateMember } = useTeamStore();
  const [values, setValues] = useState<string[]>(initial?.values ?? []);
  const [phrases, setPhrases] = useState<string[]>(initial?.typicalPhrases ?? []);
  const [emoji, setEmoji] = useState(initial?.avatarEmoji ?? '🙂');
  const [color, setColor] = useState<AvatarColor>(initial?.avatarColor ?? 'amber');
  const [valInput, setValInput] = useState('');
  const [phraseInput, setPhraseInput] = useState('');

  const { register, handleSubmit, formState: { errors } } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: initial?.name ?? '',
      role: initial?.role ?? '',
      description: initial?.description ?? '',
    },
  });

  function onSubmit(data: FormData) {
    if (initial?.id) {
      updateMember(initial.id, {
        ...data,
        values,
        typicalPhrases: phrases,
        avatarEmoji: emoji,
        avatarColor: color,
      });
    } else {
      const member = addMember({
        name: data.name,
        role: data.role,
        description: data.description,
        values,
        typicalPhrases: phrases,
        suggestedEmoji: emoji,
        suggestedColor: color,
        appearanceDescription: initial?.appearanceDescription,
      }, initial?.preGeneratedAvatarUrl);
      onMemberAdded?.(member.id);
    }
    onSave();
  }

  function addTag(
    input: string,
    setInput: (v: string) => void,
    list: string[],
    setList: (v: string[]) => void,
  ) {
    const trimmed = input.trim();
    if (trimmed && !list.includes(trimmed)) {
      setList([...list, trimmed]);
    }
    setInput('');
  }

  function removeTag(list: string[], setList: (v: string[]) => void, item: string) {
    setList(list.filter((i) => i !== item));
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
      <AvatarPicker emoji={emoji} color={color} onEmojiChange={setEmoji} onColorChange={setColor} />

      <div className="space-y-1">
        <label className="text-sm font-medium">Name</label>
        <Input {...register('name')} placeholder="z.B. Der innere Kritiker" />
        {errors.name && <p className="text-xs text-destructive">{errors.name.message}</p>}
      </div>

      <div className="space-y-1">
        <label className="text-sm font-medium">Aufgabe / Rolle</label>
        <Input {...register('role')} placeholder="z.B. Hüter der Standards" />
        {errors.role && <p className="text-xs text-destructive">{errors.role.message}</p>}
      </div>

      <div className="space-y-1">
        <label className="text-sm font-medium">Beschreibung</label>
        <Textarea {...register('description')} placeholder="Wer ist dieser Anteil? Was treibt ihn an?" rows={3} />
        {errors.description && <p className="text-xs text-destructive">{errors.description.message}</p>}
      </div>

      <div className="space-y-2">
        <label className="text-sm font-medium">Werte</label>
        <div className="flex flex-wrap gap-1.5 mb-1">
          {values.map((v) => (
            <span key={v} className="inline-flex items-center gap-1 text-xs bg-muted rounded-full px-3 py-1">
              {v}
              <button type="button" onClick={() => removeTag(values, setValues, v)}>
                <X size={12} />
              </button>
            </span>
          ))}
        </div>
        <div className="flex gap-2">
          <Input
            value={valInput}
            onChange={(e) => setValInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); addTag(valInput, setValInput, values, setValues); }
            }}
            placeholder="Wert hinzufügen…"
            className="text-sm"
          />
          <Button type="button" variant="outline" size="sm" onClick={() => addTag(valInput, setValInput, values, setValues)}>
            +
          </Button>
        </div>
      </div>

      <div className="space-y-2">
        <label className="text-sm font-medium">Typische Sätze</label>
        <div className="space-y-1 mb-1">
          {phrases.map((p, i) => (
            <div key={i} className="flex items-start gap-2 text-sm italic text-foreground/80 pl-3 border-l-2 border-primary/30">
              <span className="flex-1">„{p}"</span>
              <button type="button" onClick={() => removeTag(phrases, setPhrases, p)} className="text-muted-foreground mt-0.5">
                <X size={12} />
              </button>
            </div>
          ))}
        </div>
        <div className="flex gap-2">
          <Input
            value={phraseInput}
            onChange={(e) => setPhraseInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); addTag(phraseInput, setPhraseInput, phrases, setPhrases); }
            }}
            placeholder="Typischen Satz hinzufügen…"
            className="text-sm"
          />
          <Button type="button" variant="outline" size="sm" onClick={() => addTag(phraseInput, setPhraseInput, phrases, setPhrases)}>
            +
          </Button>
        </div>
      </div>

      <div className="flex gap-2 pt-2">
        <Button type="submit" className="flex-1">Speichern</Button>
        <Button type="button" variant="outline" onClick={onCancel}>Abbrechen</Button>
      </div>
    </form>
  );
}
